import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Save, Eye } from "lucide-react";
import { toast } from "sonner";
import api, { mediaUrl, formatApiError } from "@/lib/api";
import { Field, Input, Textarea, Select, Btn, Toggle } from "@/components/admin/AdminUI";
import ImageUpload from "@/components/admin/ImageUpload";
import RichEditor from "@/components/admin/RichEditor";
import { useSite } from "@/context/SiteContext";

const EMPTY = {
  title: "", excerpt: "", content: "", cover_image: "", category_id: "", author_id: "",
  status: "draft", featured: false, reading_time: 5, gallery: [], scheduled_at: "",
  seo_title: "", meta_description: "", slug: "", og_image: "", og_title: "", og_description: "",
};

export default function ArticleEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [form, setForm] = useState(EMPTY);
  const [cats, setCats] = useState([]);
  const [authors, setAuthors] = useState([]);
  const [tab, setTab] = useState("content");
  const [saving, setSaving] = useState(false);
  const { settings } = useSite();
  const siteTz = settings.timezone || "UTC";

  useEffect(() => {
    api.get("/categories?active_only=false").then((r) => setCats(r.data || []));
    api.get("/authors?active_only=false").then((r) => setAuthors(r.data || []));
    if (id) {
      api.get("/admin/articles").then((r) => {
        const a = (r.data || []).find((x) => x.id === id);
        if (a) setForm({ ...EMPTY, ...a, gallery: a.gallery || [] });
      });
    }
  }, [id]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async (status) => {
    const finalStatus = status || form.status;
    if (finalStatus === "scheduled" && !form.scheduled_at) {
      toast.error("Selecciona una fecha de publicación en la pestaña Ajustes");
      setTab("settings");
      return;
    }
    setSaving(true);
    const payload = { ...form, status: finalStatus, scheduled_at: form.scheduled_at || null, reading_time: Number(form.reading_time) || 5 };
    delete payload.category; delete payload.author; delete payload.published_at;
    delete payload.created_at; delete payload.updated_at; delete payload.related; delete payload.id;
    delete payload.created_by;
    try {
      if (id) await api.put(`/admin/articles/${id}`, payload);
      else await api.post("/admin/articles", payload);
      toast.success(finalStatus === "scheduled" ? "Artículo programado" : "Artículo guardado");
      navigate("/admin/articulos");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const addGallery = (url) => { if (url) set("gallery", [...(form.gallery || []), url]); };
  const removeGallery = (i) => set("gallery", form.gallery.filter((_, x) => x !== i));
  const isScheduled = form.status === "scheduled";

  return (
    <div className="max-w-4xl">
      <button onClick={() => navigate("/admin/articulos")} className="flex items-center gap-2 text-sm text-sunset-muted hover:text-sunset-ink mb-4">
        <ArrowLeft size={16} /> Volver
      </button>
      <div className="flex items-center justify-between mb-8">
        <h1 className="font-serif text-4xl tracking-tight">{id ? "Editar" : "Nuevo"} artículo</h1>
        <div className="flex gap-2">
          {id && (
            <Btn variant="ghost" onClick={() => window.open(`/admin/preview/${id}`, "_blank")} data-testid="preview-article-btn"><Eye size={15} /> Vista previa</Btn>
          )}
          <Btn variant="ghost" onClick={() => save("draft")} disabled={saving} data-testid="save-draft-btn">Guardar borrador</Btn>
          {isScheduled ? (
            <Btn variant="accent" onClick={() => save("scheduled")} disabled={saving} data-testid="schedule-article-btn"><Save size={15} /> Programar</Btn>
          ) : (
            <Btn variant="accent" onClick={() => save("published")} disabled={saving} data-testid="publish-article-btn"><Save size={15} /> Publicar</Btn>
          )}
        </div>
      </div>

      <div className="flex gap-1 border-b border-sunset-border mb-6">
        {[["content", "Contenido"], ["settings", "Ajustes"], ["seo", "SEO"]].map(([k, l]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`editor-tab-${k}`}
            className={`px-5 py-3 text-xs uppercase tracking-wider font-semibold border-b-2 -mb-px transition-colors ${tab === k ? "border-sunset-accent text-sunset-ink" : "border-transparent text-sunset-muted"}`}>{l}</button>
        ))}
      </div>

      {tab === "content" && (
        <div>
          <Field label="Título"><Input data-testid="article-title" value={form.title} onChange={(e) => set("title", e.target.value)} /></Field>
          <Field label="Extracto / Subtítulo"><Textarea rows={2} value={form.excerpt} onChange={(e) => set("excerpt", e.target.value)} /></Field>
          <div className="mb-4"><ImageUpload label="Imagen principal" value={form.cover_image} onChange={(v) => set("cover_image", v)} /></div>
          <Field label="Contenido"><RichEditor value={form.content} onChange={(v) => set("content", v)} /></Field>
          <div className="mt-4">
            <span className="block text-xs font-semibold uppercase tracking-wider text-sunset-muted mb-2">Galería de imágenes</span>
            <div className="flex flex-wrap gap-3 mb-3">
              {(form.gallery || []).map((g, i) => (
                <div key={i} className="relative w-20 h-20 bg-sunset-border group">
                  <img src={mediaUrl(g)} alt="" className="w-full h-full object-cover" />
                  <button onClick={() => removeGallery(i)} className="absolute top-0 right-0 bg-black/60 text-white text-xs px-1.5 opacity-0 group-hover:opacity-100">×</button>
                </div>
              ))}
            </div>
            <ImageUpload label="Añadir a galería" value="" onChange={addGallery} />
          </div>
        </div>
      )}

      {tab === "settings" && (
        <div className="max-w-lg">
          <Field label="Categoría">
            <Select value={form.category_id} onChange={(e) => set("category_id", e.target.value)} data-testid="article-category">
              <option value="">Sin categoría</option>
              {cats.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </Select>
          </Field>
          <Field label="Autor">
            <Select value={form.author_id} onChange={(e) => set("author_id", e.target.value)} data-testid="article-author">
              <option value="">Sin autor</option>
              {authors.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
            </Select>
          </Field>
          <Field label="Estado">
            <Select value={form.status} onChange={(e) => set("status", e.target.value)} data-testid="article-status">
              <option value="draft">Borrador</option>
              <option value="scheduled">Programado</option>
              <option value="published">Publicado</option>
              <option value="archived">Archivado</option>
            </Select>
          </Field>
          {form.status === "scheduled" && (
            <Field label="Fecha de publicación" hint={`Se interpreta en la zona horaria del sitio: ${siteTz}`}><Input type="datetime-local" data-testid="article-scheduled-at" value={form.scheduled_at || ""} onChange={(e) => set("scheduled_at", e.target.value)} /></Field>
          )}
          <Field label="Tiempo de lectura (min)"><Input type="number" value={form.reading_time} onChange={(e) => set("reading_time", e.target.value)} /></Field>
          <div className="py-2"><Toggle checked={form.featured} onChange={(v) => set("featured", v)} label="Marcar como destacado" /></div>
        </div>
      )}

      {tab === "seo" && (
        <div className="max-w-lg">
          <Field label="Slug (URL)" hint="Se genera automáticamente del título si se deja vacío"><Input value={form.slug} onChange={(e) => set("slug", e.target.value)} placeholder="mi-articulo" /></Field>
          <Field label="SEO Title"><Input value={form.seo_title} onChange={(e) => set("seo_title", e.target.value)} /></Field>
          <Field label="Meta Description"><Textarea rows={2} value={form.meta_description} onChange={(e) => set("meta_description", e.target.value)} /></Field>
          <div className="mb-4"><ImageUpload label="Open Graph Image" value={form.og_image} onChange={(v) => set("og_image", v)} /></div>
          <Field label="OG Title"><Input value={form.og_title} onChange={(e) => set("og_title", e.target.value)} /></Field>
          <Field label="OG Description"><Textarea rows={2} value={form.og_description} onChange={(e) => set("og_description", e.target.value)} /></Field>
        </div>
      )}
    </div>
  );
}
